import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { Dropdown } from "~/components/V2/Dropdown";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import type { PetInstance } from "~/mobx/entities/pets/Pet";
import { useStore } from "~/mobx/utils/useStore";
import { EditPetModal } from "./EditPetModal";

export const InsuranceCompanyPickerModal = observer(
  function InsuranceCompanyPickerModal({
    isVisible,
    onClose,
    initialInsuranceCompanyId,
    pet,
  }: {
    isVisible: boolean;
    onClose: () => void;
    initialInsuranceCompanyId?: string | null;
    pet: PetInstance;
  }) {
    const store = useStore();
    const errorAlert = useErrorAlertV2();
    // const [isPickerOpened, setIsPickerOpened] = useState(false);
    const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState(
      initialInsuranceCompanyId,
    );

    const query = useQuery({
      queryKey: ["insuranceCompanies"],
      queryFn: async () => {
        const companyList =
          await store.insuranceCompanyStore.getInsuranceCompanyList();
        return companyList;
      },
    });

    if (!isVisible) {
      return null;
    }

    if (query.isLoading) {
      return (
        <View absoluteTopLeft absoluteBottomRight flex>
          <SpinnerV2 />
        </View>
      );
    }

    if (query.isError) {
      return (
        <View
          absoluteTopLeft
          absoluteBottomRight
          flex
          colorWhite
          style={{ zIndex: 100 }}
        >
          <ErrorViewV2 error={query.error} />
          <Spacer />
          <View centerContent>
            <ButtonV2
              onPress={() => {
                onClose();
                query.refetch();
              }}
              title={intl.formatMessage({
                id: "9hKhyZ",
                defaultMessage: "Try Again",
                description: "Button to retry loading the insurance companies",
              })}
            />
          </View>
        </View>
      );
    }

    // const selectedInsuranceCompanyName = query.data?.find(
    //   (company) => company.id === selectedInsuranceCompany,
    // )?.name;

    const insuranceCompanyList =
      query.data?.map((company) => ({
        id: company.id,
        title: company.name,
      })) ?? [];

    return (
      <>
        {isVisible && (
          <EditPetModal
            onSave={async () => {
              if (selectedInsuranceCompany) {
                try {
                  await pet.updatePet({
                    insuranceCompanyId: selectedInsuranceCompany,
                  });
                } catch (error) {
                  console.log(error);
                  errorAlert(error);
                  Sentry.captureException(error);
                }
              }

              onClose();
            }}
            onClose={onClose}
            title={intl.formatMessage({
              id: "qYD8AK",
              defaultMessage: "Update insurance company",
              description:
                "Field for indicating the insurance company of a pet.",
            })}
          >
            {/* Insurance Company Picker */}
            <Dropdown
              items={insuranceCompanyList}
              label={intl.formatMessage({
                id: "lRhA7i",
                defaultMessage: "Insurance company",
                description: "Label for the insurance company picker",
              })}
              onSelectItem={(item) => {
                setSelectedInsuranceCompany(item);
              }}
              initialSelectedItemId={initialInsuranceCompanyId}
              selectedItemId={selectedInsuranceCompany}
              floating={false}
            />
          </EditPetModal>
        )}
      </>
    );
  },
);
